import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, ChevronRight, Copy, Check, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const REWARD_INR = 500;
const NEXT_MILESTONE = 3; // 3 successful referrals unlocks next tier

export default function GrowthWidget() {
  const [profile, setProfile] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referrals/me").then((r) => setProfile(r.data)).catch(() => {});
  }, []);

  if (!profile) return null;

  const completed = profile.referrals_completed || 0;
  const toNext = Math.max(0, NEXT_MILESTONE - completed);
  const progress = Math.min(100, (completed / NEXT_MILESTONE) * 100);
  const shareUrl = `${window.location.origin}/?ref=${profile.code}`;

  const copy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success("Link copied");
  };

  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#FF7A00] via-[#FF8F1F] to-[#FFAE4A] text-white p-5"
      data-testid="growth-widget"
    >
      <div className="absolute -top-16 -right-12 w-40 h-40 rounded-full bg-white/20 blur-2xl" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-1">
          <Gift className="w-4 h-4" />
          <div className="text-xs font-bold uppercase tracking-wider opacity-90">Referral Rewards</div>
        </div>

        <div className="font-heading font-black text-3xl leading-tight" data-testid="growth-earned">
          ₹{(profile.rewards_earned_inr || 0).toLocaleString("en-IN")}
        </div>
        <div className="text-xs opacity-90">Earned so far · {completed} successful referrals</div>

        {/* Progress bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest opacity-90 mb-1.5">
            <span>Next milestone</span>
            <span>{completed} / {NEXT_MILESTONE}</span>
          </div>
          <div className="h-2 rounded-full bg-white/20 overflow-hidden">
            <div className="h-full bg-white rounded-full transition-all duration-700" style={{ width: `${progress}%` }} data-testid="growth-progress" />
          </div>
          <div className="text-[11px] mt-1.5 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            {toNext > 0
              ? <>{toNext} more referral{toNext > 1 ? "s" : ""} to unlock ₹{(NEXT_MILESTONE * REWARD_INR).toLocaleString("en-IN")}</>
              : <>Milestone hit! Keep going for the next tier</>
            }
          </div>
        </div>

        {/* Share code */}
        <div className="mt-4 flex items-center gap-2 bg-white/15 rounded-lg px-2.5 py-1.5">
          <span className="font-mono font-bold text-sm flex-1 truncate" data-testid="growth-code">{profile.code}</span>
          <button
            onClick={copy}
            className="p-1 rounded hover:bg-white/15"
            aria-label="Copy referral link"
            data-testid="growth-copy"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>

        <Link
          to="/rewards"
          className="mt-4 inline-flex items-center gap-1 text-sm font-bold hover:underline"
          data-testid="growth-open-rewards"
        >
          Manage rewards <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
